import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { BookingContext, isFormSubmitted, markFormSubmitted, openPaymentPage, openTallyPopup } from "@/booking";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { StatsBar } from "@/components/StatsBar";
import { About } from "@/components/About";
import { Audiences } from "@/components/Audiences";
import { MissionVision } from "@/components/MissionVision";
import { Services } from "@/components/Services";
import { Benefits } from "@/components/Benefits";
import { Differentiators } from "@/components/Differentiators";
import { Vision } from "@/components/Vision";
import { Values } from "@/components/Values";
import { FAQ } from "@/components/FAQ";
import { FinalCTA } from "@/components/FinalCTA";
import { Footer } from "@/components/Footer";
import { BookingModal } from "@/components/BookingModal";

function App() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const lastTrigger = useRef(0);

  const requestBooking = () => {
    const now = Date.now();
    if (now - lastTrigger.current < 600) return;
    lastTrigger.current = now;

    if (isFormSubmitted()) {
      openPaymentPage();
      return;
    }

    openTallyPopup();
  };

  useEffect(() => {
    const handleMessage = (e) => {
      try {
        const isSubmitted =
          (typeof e.data === "string" && e.data.includes("Tally.FormSubmitted")) ||
          (typeof e.data === "object" && e.data !== null && e.data.event === "Tally.FormSubmitted");

        if (isSubmitted) {
          markFormSubmitted();
          openPaymentPage();
        }
      } catch {}
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (bookingOpen) return;
    const onClick = (e) => {
      if (isFormSubmitted()) return;
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('header, footer, [role="dialog"], [data-no-booking], [data-testid^="faq-"], [data-sonner-toaster]')) return;
      requestBooking();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [bookingOpen]);

  return (
    <BookingContext.Provider value={requestBooking}>
      <div className="overflow-x-clip bg-cream font-body text-ink antialiased">
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-40 opacity-[0.05] mix-blend-multiply"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E")`,
          }}
        />
        <Nav />
        <main>
          <Hero />
          <Marquee />
          <StatsBar />
          <About />
          <Audiences />
          <MissionVision />
          <Services />
          <Benefits />
          <Differentiators />
          <Vision />
          <Values />
          <FAQ />
          <FinalCTA />
        </main>
        <Footer />
        <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
        <Toaster position="top-center" richColors />
      </div>
    </BookingContext.Provider>
  );
}

export default App;
